import { useId } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import PromoTileCard from './PromoTileCard';
import type { PromoTilesData } from './types';

import styles from './PromoTiles.module.scss';

export type PromoTilesProps = PromoTilesData;

/** The authored range the design documents for this section. */
const MIN_TILES = 1;
const MAX_TILES = 4;

/**
 * A hardcoded pixel mirror of the Sass breakpoints, because a `sizes` media
 * condition cannot read a Sass map.
 */
const IMAGE_SIZES: Record<number, string> = {
  1: '100vw',
  2: '(min-width: 988px) 50vw, (min-width: 720px) 50vw, 100vw',
  3: '(min-width: 988px) 33vw, (min-width: 720px) 50vw, 100vw',
};
const FOUR_UP_IMAGE_SIZES =
  '(min-width: 988px) 25vw, (min-width: 720px) 50vw, 100vw';

/**
 * ED-225 — Promo Tiles.
 *
 * A grid of 1–4 image tiles with the copy overlaid on the photo, shared by the
 * CIP and the CLP. The authored tile count drives the column tracks, so two
 * tiles are halves and three are thirds rather than whatever the viewport
 * happens to allow.
 *
 * The section headline is optional: with none authored the region falls back to
 * an `aria-label`.
 */
const PromoTiles = ({ heading, tiles }: PromoTilesProps) => {
  const { t } = useTranslation('common');
  const headingId = useId();
  const { asPath } = useRouter();

  const interactionLocation = asPath.split('?')[0];

  if (
    process.env.NODE_ENV !== 'production' &&
    (tiles.length < MIN_TILES || tiles.length > MAX_TILES)
  ) {
    console.warn(
      `PromoTiles: ${tiles.length} tiles supplied; the design specifies ${MIN_TILES}-${MAX_TILES}.`
    );
  }

  if (!tiles.length) return null;
  const sizes = IMAGE_SIZES[tiles.length] ?? FOUR_UP_IMAGE_SIZES;

  return (
    <section
      className={styles.root}
      data-single={tiles.length === 1}
      aria-labelledby={heading ? headingId : undefined}
      aria-label={heading ? undefined : t('promoTiles.label')}
      data-testid="promo-tiles"
    >
      <div className={styles.inner}>
        {heading && (
          <h2
            id={headingId}
            className={styles.heading}
            data-single={tiles.length === 1}
          >
            {heading}
          </h2>
        )}
        <ul className={styles.grid} data-count={tiles.length}>
          {tiles.map((tile, index) => (
            <li
              key={`${tile.heading}-${tile.ctas[0]?.url ?? index}`}
              className={styles.tile}
            >
              <PromoTileCard
                tile={tile}
                title={heading}
                headingTag={heading ? 'h3' : 'h2'}
                tileCount={tiles.length}
                sizes={sizes}
                interactionLocation={interactionLocation}
                index={index}
              />
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
};

export default PromoTiles;
